import React, { useState, useEffect } from "react";
import { RadioButton } from "primereact/radiobutton";
import { InputTextarea } from "primereact/inputtextarea";
import { saveSectionData, loadSectionData } from "../utils/answerManager";
import { stripMarkdown } from "../utils/brsrAiQuestions";

const SectionB = ({ reportId }) => {
  const storageReportId = reportId || localStorage.getItem("brsr_report_id");
  // Generate P1-P9 column headers
  const pColumns = Array.from({ length: 9 }, (_, i) => `P${i + 1}`);

  /** Pad Tab 1 rows to 19 columns (1 + 9 + 9) so the grid aligns with Q10–12. */
  const renderTab1ExtendedPadCells = (questionKey) =>
    pColumns.map((_, i) => (
      <td key={`tab1-pad-${questionKey}-${i}`} className="section-b-grid-pad-cell" aria-hidden="true" />
    ));

  // State for currency options (if needed in future)
  const [currencyOptions, setCurrencyOptions] = useState([]);

  // Active row state for collapsible behavior
  const [activeRow, setActiveRow] = useState(null);

  // Helper function to validate numbers only (no letters or special characters except decimal point)
  const validateNumbersOnly = (value) => {
    if (value === "") return true;
    // Allow only digits and one decimal point
    return /^\d*\.?\d*$/.test(value);
  };

  // Helper function to validate integers only (no decimals, no letters)
  const validateIntegersOnly = (value) => {
    if (value === "") return true;
    // Allow only digits
    return /^\d+$/.test(value);
  };

  // Fetch Currencies from API (if needed in future)
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}currencies`);
        if (response.ok) {
          const data = await response.json();
          if (data && Array.isArray(data)) {
            // If API returns array of currency objects
            const options = data.map((item) => ({
              label: item.code ? `${item.code}${item.symbol ? ` (${item.symbol})` : ""}` : item.name || item,
              value: item.code || item.value || item,
            }));
            setCurrencyOptions(options);
          } else if (data.data && Array.isArray(data.data)) {
            // If API returns { data: [...] }
            const options = data.data.map((item) => ({
              label: item.code ? `${item.code}${item.symbol ? ` (${item.symbol})` : ""}` : item.name || item,
              value: item.code || item.value || item,
            }));
            setCurrencyOptions(options);
          }
        }
      } catch (error) {
        console.error("Error fetching currencies:", error);
        // Fallback to default currencies if API fails
        setCurrencyOptions([
          { label: "INR (₹)", value: "INR" },
          { label: "USD ($)", value: "USD" },
          { label: "EUR (€)", value: "EUR" },
          { label: "GBP (£)", value: "GBP" },
          { label: "JPY (¥)", value: "JPY" },
        ]);
      }
    };

    fetchCurrencies();
  }, []);

  // Helper to create initial question data structure - separate storage for each answer type
  const createQuestionData = (defaultType) => ({
    answerType: defaultType,
    yesnoValues: Array(9).fill(""),
    perPrincipleValues: Array(9).fill(""),
    commonValue: "",
  });

  const normalizeQuestionData = (raw, defaultType) => {
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      const nextType = raw.answerType || defaultType;

      // Handle old format (single values array) - migrate to new format
      if (Array.isArray(raw.values) && !raw.yesnoValues && !raw.perPrincipleValues) {
        const values = raw.values.slice(0, 9);
        while (values.length < 9) values.push("");
        return {
          answerType: nextType,
          yesnoValues: nextType === "yesno" ? values : Array(9).fill(""),
          perPrincipleValues: nextType === "perprinciple" ? values : Array(9).fill(""),
          commonValue: raw.commonValue || "",
        };
      }

      // Handle new format with separate storage
      const yesnoVals = Array.isArray(raw.yesnoValues) ? raw.yesnoValues.slice(0, 9) : Array(9).fill("");
      while (yesnoVals.length < 9) yesnoVals.push("");

      const perPrincipleVals = Array.isArray(raw.perPrincipleValues) ? raw.perPrincipleValues.slice(0, 9) : Array(9).fill("");
      while (perPrincipleVals.length < 9) perPrincipleVals.push("");

      return {
        answerType: nextType,
        yesnoValues: yesnoVals,
        perPrincipleValues: perPrincipleVals,
        commonValue: raw.commonValue || "",
      };
    }
    if (Array.isArray(raw)) {
      const nextValues = raw.slice(0, 9);
      while (nextValues.length < 9) nextValues.push("");
      return {
        answerType: defaultType,
        yesnoValues: defaultType === "yesno" ? nextValues : Array(9).fill(""),
        perPrincipleValues: defaultType === "perprinciple" ? nextValues : Array(9).fill(""),
        commonValue: "",
      };
    }
    return createQuestionData(defaultType);
  };

  const normalizeQ10RowData = (raw) => {
    const defaultRow = {
      answerType: "perprinciple",
      review: createQuestionData("perprinciple"),
      frequency: createQuestionData("perprinciple"),
    };
    if (!raw || typeof raw !== "object") return defaultRow;

    if (raw.review && Array.isArray(raw.review) && raw.frequency && Array.isArray(raw.frequency)) {
      return {
        answerType: "perprinciple",
        review: normalizeQuestionData({ perPrincipleValues: raw.review, answerType: "perprinciple" }, "perprinciple"),
        frequency: normalizeQuestionData({ perPrincipleValues: raw.frequency, answerType: "perprinciple" }, "perprinciple"),
      };
    }

    return {
      answerType: raw.answerType || "perprinciple",
      review: normalizeQuestionData(raw.review, "perprinciple"),
      frequency: normalizeQuestionData(raw.frequency, "perprinciple"),
    };
  };

  // State for Tab 1: Policy and management processes - New data structure with answerType
  const [tab1Data, setTab1Data] = useState({
    q1a: createQuestionData("yesno"),
    q1b: createQuestionData("yesno"),
    q1c: createQuestionData("perprinciple"),
    q2: createQuestionData("yesno"),
    q3: createQuestionData("yesno"),
    q4: createQuestionData("perprinciple"),
    q5: createQuestionData("perprinciple"),
    q6: createQuestionData("perprinciple"),
  });

  // State for Tab 2: Governance, leadership and oversight
  const [tab2Data, setTab2Data] = useState({
    q7: "", // Single text input - no answer type needed
    q8: "", // Single text input - no answer type needed
    q9: "", // Single text input - no answer type needed
    q10: {
      row1: normalizeQ10RowData(),
      row2: normalizeQ10RowData(),
    },
    q11: createQuestionData("yesno"),
    q12: {
      row1: createQuestionData("yesno"),
      row2: createQuestionData("yesno"),
      row3: createQuestionData("yesno"),
      row4: createQuestionData("yesno"),
      row5: createQuestionData("perprinciple"),
    },
  });

  // Load saved data on mount
  useEffect(() => {
    const saved = loadSectionData(storageReportId, "B");
    if (saved) {
      if (saved.tab1Data) {
        setTab1Data({
          q1a: normalizeQuestionData(saved.tab1Data.q1a, "yesno"),
          q1b: normalizeQuestionData(saved.tab1Data.q1b, "yesno"),
          q1c: normalizeQuestionData(saved.tab1Data.q1c, "perprinciple"),
          q2: normalizeQuestionData(saved.tab1Data.q2, "yesno"),
          q3: normalizeQuestionData(saved.tab1Data.q3, "yesno"),
          q4: normalizeQuestionData(saved.tab1Data.q4, "perprinciple"),
          q5: normalizeQuestionData(saved.tab1Data.q5, "perprinciple"),
          q6: normalizeQuestionData(saved.tab1Data.q6, "perprinciple"),
        });
      }
      if (saved.tab2Data) {
        setTab2Data((prev) => ({
          ...prev,
          q7: saved.tab2Data.q7 || "",
          q8: saved.tab2Data.q8 || "",
          q9: saved.tab2Data.q9 || "",
          q10: {
            row1: normalizeQ10RowData(saved.tab2Data.q10?.row1),
            row2: normalizeQ10RowData(saved.tab2Data.q10?.row2),
          },
          q11: normalizeQuestionData(saved.tab2Data.q11, "yesno"),
          q12: {
            row1: normalizeQuestionData(saved.tab2Data.q12?.row1, "yesno"),
            row2: normalizeQuestionData(saved.tab2Data.q12?.row2, "yesno"),
            row3: normalizeQuestionData(saved.tab2Data.q12?.row3, "yesno"),
            row4: normalizeQuestionData(saved.tab2Data.q12?.row4, "yesno"),
            row5: normalizeQuestionData(saved.tab2Data.q12?.row5, "perprinciple"),
          },
        }));
      }
    }
  }, [storageReportId]);

  // Auto-save data when it changes
  useEffect(() => {
    const dataToSave = { tab1Data, tab2Data };
    const timeoutId = setTimeout(() => {
      saveSectionData(storageReportId, "B", dataToSave);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [tab1Data, tab2Data, storageReportId]);

  // Update handlers for Tab 1 - Updated for new data structure with separate storage
  const updateTab1AnswerType = (question, answerType) => {
    setTab1Data((prev) => ({
      ...prev,
      [question]: {
        ...prev[question],
        answerType,
      },
    }));
  };

  const updateTab1Value = (question, pIndex, value) => {
    setTab1Data((prev) => {
      const currentData = prev[question];
      const answerType = currentData.answerType;

      if (answerType === "yesno") {
        return {
          ...prev,
          [question]: {
            ...currentData,
            yesnoValues: currentData.yesnoValues.map((val, idx) => (idx === pIndex ? value : val)),
          },
        };
      } else if (answerType === "perprinciple") {
        return {
          ...prev,
          [question]: {
            ...currentData,
            perPrincipleValues: currentData.perPrincipleValues.map((val, idx) => (idx === pIndex ? value : val)),
          },
        };
      }

      return prev;
    });
  };

  const updateTab1CommonValue = (question, value) => {
    setTab1Data((prev) => ({
      ...prev,
      [question]: {
        ...prev[question],
        commonValue: value,
      },
    }));
  };

  // Update handlers for Tab 2
  const updateTab2Field = (field, value) => {
    setTab2Data((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateTab2Q10AnswerType = (row, answerType) => {
    setTab2Data((prev) => {
      const current = prev.q10[row];
      return {
        ...prev,
        q10: {
          ...prev.q10,
          [row]: {
            ...current,
            answerType,
            review: normalizeQuestionData({ ...current.review, answerType }, answerType),
            frequency: normalizeQuestionData({ ...current.frequency, answerType }, answerType),
          },
        },
      };
    });
  };

  const updateTab2Q10Value = (row, field, pIndex, value) => {
    setTab2Data((prev) => {
      const currentFieldData = prev.q10[row][field];
      const answerType = currentFieldData.answerType;

      if (answerType === "yesno") {
        return {
          ...prev,
          q10: {
            ...prev.q10,
            [row]: {
              ...prev.q10[row],
              [field]: {
                ...currentFieldData,
                yesnoValues: currentFieldData.yesnoValues.map((val, idx) => (idx === pIndex ? value : val)),
              },
            },
          },
        };
      } else if (answerType === "perprinciple") {
        return {
          ...prev,
          q10: {
            ...prev.q10,
            [row]: {
              ...prev.q10[row],
              [field]: {
                ...currentFieldData,
                perPrincipleValues: currentFieldData.perPrincipleValues.map((val, idx) => (idx === pIndex ? value : val)),
              },
            },
          },
        };
      }

      return prev;
    });
  };

  const updateTab2Q10CommonValue = (row, field, value) => {
    setTab2Data((prev) => ({
      ...prev,
      q10: {
        ...prev.q10,
        [row]: {
          ...prev.q10[row],
          [field]: {
            ...prev.q10[row][field],
            commonValue: value,
          },
        },
      },
    }));
  };

  const updateTab2Q11AnswerType = (answerType) => {
    setTab2Data((prev) => ({
      ...prev,
      q11: {
        ...prev.q11,
        answerType,
      },
    }));
  };

  const updateTab2Q11Value = (pIndex, value) => {
    setTab2Data((prev) => {
      const currentData = prev.q11;
      const answerType = currentData.answerType;

      if (answerType === "yesno") {
        return {
          ...prev,
          q11: {
            ...currentData,
            yesnoValues: currentData.yesnoValues.map((val, idx) => (idx === pIndex ? value : val)),
          },
        };
      } else if (answerType === "perprinciple") {
        return {
          ...prev,
          q11: {
            ...currentData,
            perPrincipleValues: currentData.perPrincipleValues.map((val, idx) => (idx === pIndex ? value : val)),
          },
        };
      }

      return prev;
    });
  };

  const updateTab2Q11CommonValue = (value) => {
    setTab2Data((prev) => ({
      ...prev,
      q11: {
        ...prev.q11,
        commonValue: value,
      },
    }));
  };

  const updateTab2Q12AnswerType = (row, answerType) => {
    setTab2Data((prev) => ({
      ...prev,
      q12: {
        ...prev.q12,
        [row]: {
          ...prev.q12[row],
          answerType,
        },
      },
    }));
  };

  const updateTab2Q12Value = (row, pIndex, value) => {
    setTab2Data((prev) => {
      const currentData = prev.q12[row];
      const answerType = currentData.answerType;

      if (answerType === "yesno") {
        return {
          ...prev,
          q12: {
            ...prev.q12,
            [row]: {
              ...currentData,
              yesnoValues: currentData.yesnoValues.map((val, idx) => (idx === pIndex ? value : val)),
            },
          },
        };
      } else if (answerType === "perprinciple") {
        return {
          ...prev,
          q12: {
            ...prev.q12,
            [row]: {
              ...currentData,
              perPrincipleValues: currentData.perPrincipleValues.map((val, idx) => (idx === pIndex ? value : val)),
            },
          },
        };
      }

      return prev;
    });
  };

  const updateTab2Q12CommonValue = (row, value) => {
    setTab2Data((prev) => ({
      ...prev,
      q12: {
        ...prev.q12,
        [row]: {
          ...prev.q12[row],
          commonValue: value,
        },
      },
    }));
  };

  const getDisplayValue = (data, index) => {
    if (!data) return "-";
    if (data.answerType === "common") {
      const raw = data.commonValue ? stripMarkdown(data.commonValue) : "";
      return raw || "-";
    }
    const values = data.answerType === "yesno" ? data.yesnoValues : data.perPrincipleValues;
    const raw = values && values[index] ? stripMarkdown(values[index]) : "";
    return raw || "-";
  };

  // Render Yes/No inputs directly in each table column (no boxed cards)
  const renderYesNoCells = (questionKey, data, updateValue, colSpan = 1) =>
    pColumns.map((pCol, index) => (
      <td key={`yesno-cell-${questionKey}-${index}`} colSpan={colSpan} className="section-a-dynamic-table-cell">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <RadioButton inputId={`yesno-${questionKey}-${pCol}-yes`} value="Yes" checked={data.yesnoValues[index] === "Yes"} onChange={() => updateValue(questionKey, index, "Yes")} />
            <label htmlFor={`yesno-${questionKey}-${pCol}-yes`} style={{ cursor: "pointer", margin: 0, fontSize: "12px" }}>
              Yes
            </label>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <RadioButton inputId={`yesno-${questionKey}-${pCol}-no`} value="No" checked={data.yesnoValues[index] === "No"} onChange={() => updateValue(questionKey, index, "No")} />
            <label htmlFor={`yesno-${questionKey}-${pCol}-no`} style={{ cursor: "pointer", margin: 0, fontSize: "12px" }}>
              No
            </label>
          </div>
        </div>
      </td>
    ));

  // Render Common Answer input for expanded row
  const renderCommonInput = (questionKey, data, updateCommon) => <InputTextarea value={data.commonValue || ""} onChange={(e) => updateCommon(questionKey, e.target.value)} rows={4} style={{ width: "100%" }} className="section-a-input" placeholder="Enter common answer for all principles..." />;

  // Render Per Principle input list for expanded row
  const renderPerPrincipleInput = (questionKey, data, updateValue) => (
    <div className="section-b-perprinciple-list">
      {pColumns.map((pCol, index) => (
        <div key={index} className="section-b-perprinciple-row">
          <span className="section-b-principle-badge">{pCol}</span>
          <InputTextarea value={data.perPrincipleValues[index] || ""} onChange={(e) => updateValue(questionKey, index, e.target.value)} rows={2} style={{ flex: 1 }} className="section-a-input" placeholder={`Enter answer for ${pCol}...`} />
        </div>
      ))}
    </div>
  );

  // Render answer type selector with pill-style buttons
  const renderAnswerTypeSelector = (questionKey, data, isYesNoQuestion, updateAnswerType) => {
    const handleTypeChange = (type) => {
      updateAnswerType(questionKey, type);
      if (type === "perprinciple") {
        setActiveRow(questionKey);
      } else if (activeRow === questionKey) {
        setActiveRow(null);
      }
    };

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginTop: "16px",
          marginBottom: "12px",
        }}
      >
        <div className="d-block" style={{ fontSize: "12px", color: "#6B7280", marginRight: "4px" }}>
          Answer as
        </div>
        <div
          style={{
            display: "inline-flex",
            backgroundColor: "#F3F4F6",
            borderRadius: "20px",
            gap: "4px",
            border: "1px solid #D6E0DD",
          }}
        >
          {isYesNoQuestion && (
            <button
              type="button"
              onClick={() => handleTypeChange("yesno")}
              style={{
                padding: "6px !important",
                borderRadius: "16px",
                border: "none",
                fontSize: "11px!important",
                fontWeight: "500!important",
                cursor: "pointer",
                transition: "all 0.2s",
                height: "30px",
                width: "70px",
                backgroundColor: data.answerType === "yesno" ? "#059669" : "transparent",
                color: data.answerType === "yesno" ? "#FFFFFF" : "#374151",
              }}
            >
              Yes / No
            </button>
          )}
          <button
            type="button"
            onClick={() => handleTypeChange("common")}
            style={{
              padding: "6px !important",
              borderRadius: "16px",
              border: "none",
              fontSize: "12px!important",
              fontWeight: "500!important",
              cursor: "pointer",
              transition: "all 0.2s",
              height: "30px",
              backgroundColor: data.answerType === "common" ? "#059669" : "transparent",
              color: data.answerType === "common" ? "#FFFFFF" : "#374151",
            }}
          >
            Common text
          </button>
          <button
            type="button"
            onClick={() => handleTypeChange("perprinciple")}
            style={{
              padding: "6px !important",
              borderRadius: "16px",
              border: "none",
              fontSize: "12px!important",
              fontWeight: "500!important",
              cursor: "pointer",
              transition: "all 0.2s",
              height: "30px",
              width: "100px",
              backgroundColor: data.answerType === "perprinciple" ? "#059669" : "transparent",
              color: data.answerType === "perprinciple" ? "#FFFFFF" : "#374151",
            }}
          >
            Per principle
          </button>
        </div>
      </div>
    );
  };

  const handleSelectorClick = (event) => {
    event.stopPropagation();
  };

  // Render answer input based on answer type
  const renderAnswerInput = (questionKey, data, updateValue, updateCommon) => {
    switch (data.answerType) {
      case "yesno":
        return null;
      case "common":
        return renderCommonInput(questionKey, data, updateCommon);
      case "perprinciple":
      default:
        return renderPerPrincipleInput(questionKey, data, updateValue);
    }
  };

  // Render collapsible row for Tab 1
  const renderCollapsibleRow = (questionKey, label, data, isYesNoQuestion) => {
    const isActive = activeRow === questionKey;
    const isPerPrinciple = data.answerType === "perprinciple";

    if (data.answerType === "yesno") {
      return (
        <tr key={questionKey} className="section-a-dynamic-table-row">
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, verticalAlign: "top" }}>
            {label}
            <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(questionKey, data, isYesNoQuestion, updateTab1AnswerType)}</div>
          </td>
          {renderYesNoCells(questionKey, data, updateTab1Value)}
          {renderTab1ExtendedPadCells(questionKey)}
        </tr>
      );
    }

    if (data.answerType === "common") {
      return (
        <tr key={questionKey} className="section-a-dynamic-table-row">
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, verticalAlign: "top" }}>
            {label}
            <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(questionKey, data, isYesNoQuestion, updateTab1AnswerType)}</div>
          </td>
          <td colSpan={9} className="section-a-dynamic-table-cell">
            {renderCommonInput(questionKey, data, updateTab1CommonValue)}
          </td>
          {renderTab1ExtendedPadCells(questionKey)}
        </tr>
      );
    }

    if (isPerPrinciple && !isActive) {
      // Collapsed view
      return (
        <tr key={questionKey} className="section-b-collapsed-row" onClick={() => setActiveRow(questionKey)}>
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
            {label}
            <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(questionKey, data, isYesNoQuestion, updateTab1AnswerType)}</div>
          </td>
          {pColumns.map((pCol, index) => (
            <td key={index} className="section-a-dynamic-table-cell section-b-truncated">
              {getDisplayValue(data, index)}
            </td>
          ))}
          {renderTab1ExtendedPadCells(questionKey)}
        </tr>
      );
    }

    // Expanded view (Per Principle only) — spans both P1–P9 blocks (18 cols) + question col = 19
    return (
      <tr key={questionKey} className="section-b-expanded-row">
        <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, verticalAlign: "top" }}>
          {label}
          <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(questionKey, data, isYesNoQuestion, updateTab1AnswerType)}</div>
        </td>
        <td colSpan={18} className="section-a-dynamic-table-cell">
          {renderAnswerInput(questionKey, data, updateTab1Value, updateTab1CommonValue)}
        </td>
      </tr>
    );
  };

  // Render collapsible row for Q12 (Tab 2)
  const renderQ12CollapsibleRow = (rowKey, label, data, isYesNoQuestion) => {
    const fullKey = `q12_${rowKey}`;
    const isActive = activeRow === fullKey;
    const isPerPrinciple = data.answerType === "perprinciple";

    if (data.answerType === "yesno") {
      return (
        <tr key={fullKey} className="section-a-dynamic-table-row">
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, verticalAlign: "top" }}>
            {label}
            <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(fullKey, data, isYesNoQuestion, (key, type) => updateTab2Q12AnswerType(rowKey, type))}</div>
          </td>
          {renderYesNoCells(fullKey, data, (key, pIndex, value) => updateTab2Q12Value(rowKey, pIndex, value), 2)}
        </tr>
      );
    }

    if (data.answerType === "common") {
      return (
        <tr key={fullKey} className="section-a-dynamic-table-row">
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, verticalAlign: "top" }}>
            {label}
            <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(fullKey, data, isYesNoQuestion, (key, type) => updateTab2Q12AnswerType(rowKey, type))}</div>
          </td>
          <td colSpan={18} className="section-a-dynamic-table-cell">
            {renderCommonInput(fullKey, data, (key, value) => updateTab2Q12CommonValue(rowKey, value))}
          </td>
        </tr>
      );
    }

    if (isPerPrinciple && !isActive) {
      // Collapsed view
      return (
        <tr key={fullKey} className="section-b-collapsed-row" onClick={() => setActiveRow(fullKey)}>
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
            {label}
            <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(fullKey, data, isYesNoQuestion, (key, type) => updateTab2Q12AnswerType(rowKey, type))}</div>
          </td>
          {pColumns.map((pCol, index) => (
            <td key={index} colSpan="2" className="section-a-dynamic-table-cell section-b-truncated">
              {getDisplayValue(data, index)}
            </td>
          ))}
        </tr>
      );
    }

    // Expanded view (Per Principle only)
    return (
      <tr key={fullKey} className="section-b-expanded-row">
        <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, verticalAlign: "top" }}>
          {label}
          <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(fullKey, data, isYesNoQuestion, (key, type) => updateTab2Q12AnswerType(rowKey, type))}</div>
        </td>
        <td colSpan={18} className="section-a-dynamic-table-cell">
          {renderAnswerInput(
            fullKey,
            data,
            (key, pIndex, value) => updateTab2Q12Value(rowKey, pIndex, value),
            (key, value) => updateTab2Q12CommonValue(rowKey, value),
          )}
        </td>
      </tr>
    );
  };

  const renderQ10DualInput = (rowKey, data) => (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
      <div>
        <div style={{ fontWeight: 600, marginBottom: "8px", color: "#407775" }}>Committee / Review</div>
        {data.answerType === "common" ? renderCommonInput(`q10_${rowKey}_review`, data.review, (key, value) => updateTab2Q10CommonValue(rowKey, "review", value)) : renderPerPrincipleInput(`q10_${rowKey}_review`, data.review, (key, pIndex, value) => updateTab2Q10Value(rowKey, "review", pIndex, value))}
      </div>
      <div>
        <div style={{ fontWeight: 600, marginBottom: "8px", color: "#407775" }}>Frequency</div>
        {data.answerType === "common" ? renderCommonInput(`q10_${rowKey}_frequency`, data.frequency, (key, value) => updateTab2Q10CommonValue(rowKey, "frequency", value)) : renderPerPrincipleInput(`q10_${rowKey}_frequency`, data.frequency, (key, pIndex, value) => updateTab2Q10Value(rowKey, "frequency", pIndex, value))}
      </div>
    </div>
  );

  const renderQ10CollapsibleRow = (rowKey, label, data) => {
    const fullKey = `q10_${rowKey}`;
    const isActive = activeRow === fullKey;
    const isPerPrinciple = data.answerType === "perprinciple";

    if (data.answerType === "common") {
      return (
        <tr key={fullKey} className="section-a-dynamic-table-row">
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, verticalAlign: "top" }}>
            {label}
            <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(fullKey, data, false, (key, type) => updateTab2Q10AnswerType(rowKey, type))}</div>
          </td>
          <td colSpan={18} className="section-a-dynamic-table-cell">
            {renderQ10DualInput(rowKey, data)}
          </td>
        </tr>
      );
    }

    if (isPerPrinciple && !isActive) {
      return (
        <tr key={fullKey} className="section-b-collapsed-row" onClick={() => setActiveRow(fullKey)}>
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
            {label}
            <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(fullKey, data, false, (key, type) => updateTab2Q10AnswerType(rowKey, type))}</div>
          </td>
          {pColumns.map((pCol, index) => (
            <td key={`q10-review-${rowKey}-${index}`} className="section-a-dynamic-table-cell section-b-truncated">
              {getDisplayValue(data.review, index)}
            </td>
          ))}
          {pColumns.map((pCol, index) => (
            <td key={`q10-frequency-${rowKey}-${index}`} className="section-a-dynamic-table-cell section-b-truncated">
              {getDisplayValue(data.frequency, index)}
            </td>
          ))}
        </tr>
      );
    }

    return (
      <tr key={fullKey} className="section-b-expanded-row">
        <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, verticalAlign: "top" }}>
          {label}
          <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(fullKey, data, false, (key, type) => updateTab2Q10AnswerType(rowKey, type))}</div>
        </td>
        <td colSpan={18} className="section-a-dynamic-table-cell">
          {renderQ10DualInput(rowKey, data)}
        </td>
      </tr>
    );
  };

  const renderQ11CollapsibleRow = () => {
    const fullKey = "q11";
    const isActive = activeRow === fullKey;
    const label = "11. Has the entity carried out independent assessment/ evaluation of the working of its policies by an external agency? (Yes/No). If yes, provide name of the agency.";

    if (tab2Data.q11.answerType === "yesno") {
      return (
        <tr className="section-a-dynamic-table-row">
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, verticalAlign: "top" }}>
            {label}
            <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(fullKey, tab2Data.q11, true, (key, type) => updateTab2Q11AnswerType(type))}</div>
          </td>
          {renderYesNoCells(fullKey, tab2Data.q11, (key, pIndex, value) => updateTab2Q11Value(pIndex, value), 2)}
        </tr>
      );
    }

    if (tab2Data.q11.answerType === "common") {
      return (
        <tr className="section-a-dynamic-table-row">
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, verticalAlign: "top" }}>
            {label}
            <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(fullKey, tab2Data.q11, true, (key, type) => updateTab2Q11AnswerType(type))}</div>
          </td>
          <td colSpan={18} className="section-a-dynamic-table-cell">
            {renderCommonInput(fullKey, tab2Data.q11, (key, value) => updateTab2Q11CommonValue(value))}
          </td>
        </tr>
      );
    }

    if (!isActive) {
      return (
        <tr className="section-b-collapsed-row" onClick={() => setActiveRow(fullKey)}>
          <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
            {label}
            <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(fullKey, tab2Data.q11, true, (key, type) => updateTab2Q11AnswerType(type))}</div>
          </td>
          {pColumns.map((pCol, index) => (
            <td key={`q11-${index}`} colSpan="2" className="section-a-dynamic-table-cell section-b-truncated">
              {getDisplayValue(tab2Data.q11, index)}
            </td>
          ))}
        </tr>
      );
    }

    return (
      <tr className="section-b-expanded-row">
        <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500, verticalAlign: "top" }}>
          {label}
          <div onClick={handleSelectorClick}>{renderAnswerTypeSelector(fullKey, tab2Data.q11, true, (key, type) => updateTab2Q11AnswerType(type))}</div>
        </td>
        <td colSpan={18} className="section-a-dynamic-table-cell">
          {renderAnswerInput(
            fullKey,
            tab2Data.q11,
            (key, pIndex, value) => updateTab2Q11Value(pIndex, value),
            (key, value) => updateTab2Q11CommonValue(value),
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="section-b-content">
      <div className="section-b-table-scroll">
        <table className="section-a-dynamic-table section-b-unified-table">
          <thead>
            <tr>
              <th rowSpan={2} className="section-a-dynamic-table-header section-b-sticky-corner">
                Disclosure Questions
              </th>
              <th colSpan={9} className="section-a-dynamic-table-header section-b-thead-group">
                Principles (P1–P9)
              </th>
              <th colSpan={9} className="section-a-dynamic-table-header section-b-thead-group">
                Extended (Q10–12)
              </th>
            </tr>
            <tr>
              {pColumns.map((pCol) => (
                <th key={`h1-${pCol}`} className="section-a-dynamic-table-header section-b-sticky-p">
                  {pCol}
                </th>
              ))}
              {pColumns.map((pCol) => (
                <th key={`h2-${pCol}`} className="section-a-dynamic-table-header section-b-sticky-p">
                  {pCol}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="section-b-section-banner">
              <td colSpan={19}>Policy and management processes</td>
            </tr>
            {renderCollapsibleRow("q1a", "1. a. Whether your entity's policy/policies cover each principle and its core elements of the NGRBCs. (Yes/No)", tab1Data.q1a, true)}
            {renderCollapsibleRow("q1b", "1. b. Has the policy been approved by the Board? (Yes/No)", tab1Data.q1b, true)}
            {renderCollapsibleRow("q1c", "1. c. Web Link of the Policies, if available", tab1Data.q1c, false)}
            {renderCollapsibleRow("q2", "2. Whether the entity has translated the policy into procedures. (Yes / No)", tab1Data.q2, true)}
            {renderCollapsibleRow("q3", "3. Do the enlisted policies extend to your value chain partners? (Yes/No)", tab1Data.q3, true)}
            {renderCollapsibleRow(
              "q4",
              "4. Name of the national and international codes/certifications/labels/ standards (e.g. Forest Stewardship Council, Fairtrade, Rainforest Alliance, Trustea) standards (e.g. SA 8000, OHSAS, ISO, BIS) adopted by your entity and mapped to each principle.",
              tab1Data.q4,
              false,
            )}
            {renderCollapsibleRow("q5", "5. Specific commitments, goals and targets set by the entity with defined timelines, if any.", tab1Data.q5, false)}
            {renderCollapsibleRow("q6", "6. Performance of the entity against the specific commitments, goals and targets along-with reasons in case the same are not met.", tab1Data.q6, false)}

            <tr className="section-b-section-banner">
              <td colSpan={19}>Governance, leadership and oversight</td>
            </tr>

            <tr className="section-a-dynamic-table-row">
              <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                7. Statement by director responsible for the business responsibility report, highlighting ESG related challenges, targets and achievements (listed entity has flexibility regarding the placement of this disclosure)
              </td>
              <td colSpan={18} className="section-a-dynamic-table-cell">
                <InputTextarea value={tab2Data.q7} onChange={(e) => updateTab2Field("q7", e.target.value)} className="section-a-input" rows={4} style={{ width: "100%", resize: "vertical" }} />
              </td>
            </tr>

            <tr className="section-a-dynamic-table-row">
              <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                8. Details of the highest authority responsible for implementation and oversight of the Business Responsibility policy (ies).
              </td>
              <td colSpan={18} className="section-a-dynamic-table-cell">
                <InputTextarea value={tab2Data.q8} onChange={(e) => updateTab2Field("q8", e.target.value)} className="section-a-input" rows={4} style={{ width: "100%", resize: "vertical" }} />
              </td>
            </tr>

            <tr className="section-a-dynamic-table-row">
              <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }}>
                9. Does the entity have a specified Committee of the Board/ Director responsible for decision making on sustainability related issues? (Yes/No). If yes, provide details.
              </td>
              <td colSpan={18} className="section-a-dynamic-table-cell">
                <InputTextarea value={tab2Data.q9} onChange={(e) => updateTab2Field("q9", e.target.value)} className="section-a-input" rows={4} style={{ width: "100%", resize: "vertical" }} />
              </td>
            </tr>

            <tr className="section-a-dynamic-table-row">
              <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }} colSpan={19}>
                10. Details of Review of NGRBCs by the Company:
              </td>
            </tr>

            <tr className="section-a-dynamic-table-row">
              <th className="section-a-dynamic-table-header" style={{ backgroundColor: "#EFF6F5" }} rowSpan={2}>
                Subject for Review
              </th>
              <th className="section-a-dynamic-table-header" colSpan={9} style={{ textAlign: "center", backgroundColor: "#EFF6F5" }}>
                Indicate whether review was undertaken by Director / Committee of the Board/ Any other Committee
              </th>
              <th className="section-a-dynamic-table-header" colSpan={9} style={{ textAlign: "center", backgroundColor: "#EFF6F5" }}>
                Frequency (Annually/ Half yearly/ Quarterly/ Any other – please specify)
              </th>
            </tr>
            <tr className="section-a-dynamic-table-row">
              {pColumns.map((pCol) => (
                <th key={`review-${pCol}`} className="section-a-dynamic-table-header" style={{ backgroundColor: "#EFF6F5" }}>
                  {pCol}
                </th>
              ))}
              {pColumns.map((pCol) => (
                <th key={`freq-${pCol}`} className="section-a-dynamic-table-header" style={{ backgroundColor: "#EFF6F5" }}>
                  {pCol}
                </th>
              ))}
            </tr>
            {renderQ10CollapsibleRow("row1", "Performance against above policies and follow up action", tab2Data.q10.row1)}
            {renderQ10CollapsibleRow("row2", "Compliance with statutory requirements of relevance to the principles, and, rectification of any non-compliances", tab2Data.q10.row2)}

            <tr className="section-a-dynamic-table-row">
              <th className="section-a-dynamic-table-header" style={{ backgroundColor: "#EFF6F5" }}>
                Disclosure Questions
              </th>
              {pColumns.map((pCol) => (
                <th key={`q11h-${pCol}`} className="section-a-dynamic-table-header" style={{ backgroundColor: "#EFF6F5" }} colSpan={2}>
                  {pCol}
                </th>
              ))}
            </tr>

            {renderQ11CollapsibleRow()}

            <tr className="section-a-dynamic-table-row">
              <td className="section-a-dynamic-table-cell" style={{ fontWeight: 500 }} colSpan={19}>
                12. If answer to question (1) above is "No" i.e. not all Principles are covered by a policy, reasons to be stated:
              </td>
            </tr>

            <tr className="section-a-dynamic-table-row">
              <th className="section-a-dynamic-table-header" style={{ backgroundColor: "#EFF6F5" }}>
                Questions
              </th>
              {pColumns.map((pCol) => (
                <th key={`q12h-${pCol}`} className="section-a-dynamic-table-header" style={{ backgroundColor: "#EFF6F5" }} colSpan={2}>
                  {pCol}
                </th>
              ))}
            </tr>
            {renderQ12CollapsibleRow("row1", "The entity does not consider the Principles material to its business (Yes/No)", tab2Data.q12.row1, true)}
            {renderQ12CollapsibleRow("row2", "The entity is not at a stage where it is in a position to formulate and implement the policies on specified principles (Yes/No)", tab2Data.q12.row2, true)}
            {renderQ12CollapsibleRow("row3", "The entity does not have the financial or/human and technical resources available for the task (Yes/No)", tab2Data.q12.row3, true)}
            {renderQ12CollapsibleRow("row4", "It is planned to be done in the next financial year (Yes/No)", tab2Data.q12.row4, true)}
            {renderQ12CollapsibleRow("row5", "Any other reason (please specify)", tab2Data.q12.row5, false)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SectionB;
